import React, { useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Upload, X } from "lucide-react"

interface FileUploadProps {
  id: string
  label: string
  accept?: string
  multiple?: boolean
  onChange: (files: File[]) => void
  value?: File[]
  maxFiles?: number
}

export const FileUpload: React.FC<FileUploadProps> = ({
  id,
  label,
  accept = "*",
  multiple = false,
  onChange,
  value = [],
  maxFiles,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      const combinedFiles = multiple ? [...value, ...newFiles] : newFiles

      if (maxFiles && combinedFiles.length > maxFiles) {
        // Handle max files error or truncation here if needed
        onChange(combinedFiles.slice(0, maxFiles))
      } else {
        onChange(combinedFiles)
      }
    }
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const removeFile = (index: number) => {
    const newFiles = [...value]
    newFiles.splice(index, 1)
    onChange(newFiles)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2 border-dashed"
        >
          <Upload className="h-4 w-4" />
          {label}
        </Button>
        <Input
          id={id}
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
        />
      </div>

      {value.length > 0 && (
        <div className="space-y-1">
          {value.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between rounded-lg border-l-2 border-accent/20 bg-foreground/[0.03] p-2 text-sm"
            >
              <span className="max-w-[200px] truncate">{file.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => removeFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

